/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import Effect from './Effect';

export default class BaseGLEffect extends Effect {
    constructor() {
        super();
        this._canvas = null;
        this._gl = null;
        this._program = null;

        this._frameTextureUnit = 0;
        this._frameTexture = null;

        this.vertexShaderSource = '';
        this.fragmentShaderSource = '';

        this._vertexShader = null;
        this._fragmentShader = null;
    }

    async setup(init) {
        const { canvas, gl } = init;

        if (canvas !== null && gl !== null) {
            this._canvas = canvas;
            this._gl = gl;
        }

        if (this._gl === null) {
            return Promise.reject(new Error('WebGL2 context is required'));
        }

        const program = this._gl.createProgram();
        this._program = program;

        const vertexShader = this._gl.createShader(this._gl.VERTEX_SHADER);
        this._vertexShader = vertexShader;

        if (vertexShader === null) {
            return Promise.reject(new Error('vertexShader required'));
        }

        this._gl.shaderSource(vertexShader, this.vertexShaderSource);
        this._gl.compileShader(vertexShader);

        if (program === null) {
            return Promise.reject(new Error('program required'));
        }

        this._gl.attachShader(program, vertexShader);

        const fragmentShader = this._gl.createShader(this._gl.FRAGMENT_SHADER);
        this._fragmentShader = fragmentShader;
        if (fragmentShader === null) {
            return Promise.reject(new Error('fragmentShader required'));
        }
        this._gl.shaderSource(fragmentShader, this.fragmentShaderSource);
        this._gl.compileShader(fragmentShader);
        this._gl.attachShader(program, fragmentShader);

        this._gl.linkProgram(program);

        if (!this._gl.getProgramParameter(program, this._gl.LINK_STATUS)) {
            console.error(this._gl.getShaderInfoLog(vertexShader));
            console.error(this._gl.getShaderInfoLog(fragmentShader));
        }

        this._gl.useProgram(program);

        this.setupBuffers(this._gl);
        this.setupUniforms(this._gl, program, init);
    }

    apply(form, context, _tracklets) {
        const gl = this._gl;
        if (gl === null) {
            return;
        }

        gl.clearColor(0.0, 0.0, 0.0, 1.0);
        gl.clear(gl.COLOR_BUFFER_BIT);

        gl.activeTexture(gl.TEXTURE0 + this._frameTextureUnit);
        gl.bindTexture(gl.TEXTURE_2D, this._frameTexture);
        gl.texImage2D(
            gl.TEXTURE_2D,
            0,
            gl.RGBA,
            context.frame.width,
            context.frame.height,
            0,
            gl.RGBA,
            gl.UNSIGNED_BYTE,
            context.frame,
        );

        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);

        // Apply shader
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

        const ctx = form.ctx;
        if (this._canvas === null) {
            return;
        }
        ctx.drawImage(this._canvas, 0, 0);
    }

    async cleanup() {
        if (this._gl !== null) {
            // Dispose of WebGL resources, e.g., textures, buffers, etc.
            if (this._frameTexture !== null) {
                this._gl.deleteTexture(this._frameTexture);
                this._frameTexture = null;
            }

            if (this._program !== null && this._vertexShader !== null && this._fragmentShader !== null) {
                this._gl.detachShader(this._program, this._vertexShader);
                this._gl.deleteShader(this._vertexShader);
                this._gl.detachShader(this._program, this._fragmentShader);
                this._gl.deleteShader(this._fragmentShader);
            }
        }
    }

    setupBuffers(gl) {
        const vertexBufferData = new Float32Array([1.0, 1.0, -1.0, 1.0, 1.0, -1.0, -1.0, -1.0]);

        const texCoordBufferData = new Float32Array([1.0, 1.0, 0.0, 1.0, 1.0, 0.0, 0.0, 0.0]);

        const vertexBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, vertexBufferData, gl.STATIC_DRAW);
        gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
        gl.enableVertexAttribArray(0);

        const texCoordBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, texCoordBufferData, gl.STATIC_DRAW);
        gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 0, 0);
        gl.enableVertexAttribArray(1);
    }

    setupUniforms(gl, program, init) {
        this._frameTexture = gl.createTexture();

        gl.uniform1i(gl.getUniformLocation(program, 'uSampler'), this._frameTextureUnit);

        gl.uniform2f(gl.getUniformLocation(program, 'uSize'), init.width, init.height);
    }
}
